/**
 * proveedor-retencion.ts — ap-pagar-factura-directo (T7).
 * How ONE supplier is retained for ONE retention type, when it differs from the type's own calculation:
 *   - no row            = "General": the type's calculation applies (`tipo_retencion.calculo`).
 *   - `alicuota_propia` = same mode and base as the type, but this rate (only types with a rate: porcentaje /
 *                         porcentaje_excedente; the type's `minimoNoSujeto` is kept).
 *   - `exento`          = "No retener": the suggestion is 0.
 *
 * Each entry has a validity period (migration 0009): the suggestion uses the one valid on the PAYMENT date. Saving
 * from a date closes the previous period the day before; "back to General" closes the current one yesterday.
 *
 *   GET    /sumi/cuentas-pagar/proveedores/:id/retenciones              -> ProveedorRetencionesResponse (every period)
 *   PUT    /sumi/cuentas-pagar/proveedores/:id/retenciones/:tipoId      (ProveedorRetencionUpsert) -> ProveedorRetencionItem
 *   DELETE /sumi/cuentas-pagar/proveedores/:id/retenciones/:tipoId      back to "General" from today
 */
import { z } from 'zod';
import { AlicuotaSchema } from './tipo-retencion.js';
import { IsoDateSchema } from './vocab.js';

export const PROVEEDOR_RETENCION_MODOS = ['alicuota_propia', 'exento'] as const;
export const ProveedorRetencionModoSchema = z.enum(PROVEEDOR_RETENCION_MODOS);
export type ProveedorRetencionModo = z.infer<typeof ProveedorRetencionModoSchema>;

/**
 * PUT body. Each mode carries exactly its parameters (the DB CHECK `chk_proveedor_retencion_modo` is the backstop).
 * `vigenteDesde` (default: today) — the first payment date it applies to; the same date edits that period in place.
 */
export const ProveedorRetencionUpsertSchema = z.discriminatedUnion('modo', [
  z.object({ modo: z.literal('alicuota_propia'), alicuota: AlicuotaSchema, vigenteDesde: IsoDateSchema.optional() }),
  z.object({ modo: z.literal('exento'), vigenteDesde: IsoDateSchema.optional() }),
]);
export type ProveedorRetencionUpsert = z.infer<typeof ProveedorRetencionUpsertSchema>;

export const ProveedorRetencionItemSchema = z.object({
  tipoRetencionId: z.string().uuid(),
  modo: ProveedorRetencionModoSchema,
  /** A percentage (`0.5` = 0,5 %); `null` for `exento`. */
  alicuota: z.number().nullable(),
  /** First payment date it applies to (`YYYY-MM-DD`). */
  vigenciaDesde: IsoDateSchema,
  /** Last payment date it applies to; `null` = open-ended. */
  vigenciaHasta: IsoDateSchema.nullable(),
});
export type ProveedorRetencionItem = z.infer<typeof ProveedorRetencionItemSchema>;

/** Only the types the supplier overrides, every period (by type, oldest first); a date with no period is "General". */
export const ProveedorRetencionesResponseSchema = z.object({ items: z.array(ProveedorRetencionItemSchema) });
export type ProveedorRetencionesResponse = z.infer<typeof ProveedorRetencionesResponseSchema>;
